import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  HeartIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  AcademicCapIcon,
  CakeIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BenefitsOverview = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock benefits data
  const benefitsData = {
    healthInsurance: {
      name: 'Health Insurance',
      category: 'health',
      coverage: 'Family',
      premium: 50000,
      status: 'Active',
      description: 'Comprehensive medical coverage for employee and family',
      features: [
        'Cashless treatment at 5000+ hospitals',
        'Annual health check-up',
        'Maternity benefits up to ₹50,000',
        'Pre-existing disease coverage after 2 years'
      ],
      provider: 'Apollo Munich Health Insurance',
      policyNumber: 'AMHI-2025-001234',
      validUntil: '2026-03-31'
    },
    lifeInsurance: {
      name: 'Life Insurance',
      category: 'insurance',
      coverage: '₹10,00,000',
      premium: 12000,
      status: 'Active',
      description: 'Term life insurance with accidental death benefit',
      features: [
        'Sum assured: ₹10,00,000',
        'Accidental death benefit: ₹20,00,000',
        'Terminal illness benefit',
        'Premium waiver on disability'
      ],
      provider: 'LIC of India',
      policyNumber: 'LIC-TERM-567890',
      validUntil: '2045-03-31'
    },
    providentFund: {
      name: 'Provident Fund',
      category: 'retirement',
      coverage: '12% of Basic Salary',
      contribution: 72000,
      status: 'Active',
      description: 'Retirement savings with employer matching',
      features: [
        'Employee contribution: 12% of basic salary',
        'Employer contribution: 12% of basic salary',
        'Tax benefits under Section 80C',
        'Withdrawal allowed after 5 years'
      ],
      provider: 'Employees Provident Fund Organisation',
      pfNumber: 'PF12345678',
      currentBalance: 450000
    },
    gratuity: {
      name: 'Gratuity',
      category: 'retirement',
      coverage: '4.81% of Basic Salary',
      amount: 28860,
      status: 'Active',
      description: 'Lump sum payment on completion of 5 years of service',
      features: [
        'Eligible after 5 years of continuous service',
        'Formula: 15 days salary for each year of service',
        'Tax-free up to ₹20 lakhs',
        'Payable on retirement, resignation, or death'
      ],
      provider: 'Apollo Healthcare Portal',
      eligibleAfter: '2027-03-15'
    },
    flexiBenefits: {
      name: 'Flexible Benefits',
      category: 'flexible',
      coverage: '₹25,000 annually',
      allocated: 25000,
      used: 8500,
      status: 'Active',
      description: 'Tax-saving benefits you can choose',
      options: [
        { name: 'Food Vouchers', limit: 15000, used: 5000 },
        { name: 'Transport Allowance', limit: 10000, used: 3500 },
        { name: 'Phone Reimbursement', limit: 6000, used: 0 },
        { name: 'Internet Reimbursement', limit: 4000, used: 0 }
      ]
    },
    leaveEncashment: {
      name: 'Leave Encashment',
      category: 'leave',
      coverage: 'Unused leave days',
      availableLeaves: 18,
      encashableLeaves: 12,
      status: 'Available',
      description: 'Convert unused leave days to cash',
      features: [
        'Maximum 15 days per year can be encashed',
        'Encashment rate: Current daily salary',
        'Tax applicable as per income tax rules',
        'Can be claimed quarterly'
      ]
    }
  };

  const categories = [
    { id: 'all', name: 'All Benefits', icon: CheckCircleIcon },
    { id: 'health', name: 'Health & Insurance', icon: HeartIcon },
    { id: 'retirement', name: 'Retirement', icon: BanknotesIcon },
    { id: 'flexible', name: 'Flexible Benefits', icon: AcademicCapIcon },
    { id: 'leave', name: 'Leave Benefits', icon: ClockIcon }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-green-600 bg-green-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Expired': return 'text-red-600 bg-red-100';
      case 'Available': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return CheckCircleIcon;
      case 'Pending': return ClockIcon;
      case 'Expired': return ExclamationTriangleIcon;
      case 'Available': return CheckCircleIcon;
      default: return CheckCircleIcon;
    }
  };

  const filteredBenefits = selectedCategory === 'all' 
    ? Object.values(benefitsData)
    : Object.values(benefitsData).filter(benefit => benefit.category === selectedCategory);

  const calculateTotalValue = () => {
    return Object.values(benefitsData).reduce((total, benefit) => {
      if (benefit.premium) return total + benefit.premium;
      if (benefit.contribution) return total + benefit.contribution * 2; // Include employer contribution
      if (benefit.amount) return total + benefit.amount;
      if (benefit.allocated) return total + benefit.allocated;
      return total + 0;
    }, 0);
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <button
                onClick={() => navigate('/payroll')}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Payroll
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Employee Benefits</h1>
                <p className="mt-1 sm:mt-2 text-base sm:text-lg text-gray-600">Your comprehensive benefits package</p>
              </div>
            </div>
            
            <div className="text-left sm:text-right">
              <div className="text-sm text-gray-600">Total Annual Value</div>
              <div className="text-xl sm:text-2xl font-bold text-apollo-primary">{formatCurrency(calculateTotalValue())}</div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`inline-flex items-center px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-apollo-primary text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  <span className="truncate">{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {filteredBenefits.map((benefit, index) => {
            const StatusIcon = getStatusIcon(benefit.status);
            
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-shadow duration-300">
                {/* Benefit Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${
                      benefit.category === 'health' ? 'bg-red-100' :
                      benefit.category === 'insurance' ? 'bg-blue-100' :
                      benefit.category === 'retirement' ? 'bg-green-100' :
                      benefit.category === 'flexible' ? 'bg-purple-100' :
                      'bg-gray-100'
                    }`}>
                      {benefit.category === 'health' && <HeartIcon className="h-6 w-6 text-red-600" />}
                      {benefit.category === 'insurance' && <ShieldCheckIcon className="h-6 w-6 text-blue-600" />}
                      {benefit.category === 'retirement' && <BanknotesIcon className="h-6 w-6 text-green-600" />}
                      {benefit.category === 'flexible' && <AcademicCapIcon className="h-6 w-6 text-purple-600" />}
                      {benefit.category === 'leave' && <CakeIcon className="h-6 w-6 text-orange-600" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{benefit.name}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(benefit.status)}`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {benefit.status}
                    </div>
                  </div>
                </div>

                {/* Benefit Details */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Coverage</div>
                      <div className="font-semibold text-gray-900">{benefit.coverage}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">
                        {benefit.premium ? 'Premium' :
                         benefit.contribution ? 'Annual Contribution' :
                         benefit.amount ? 'Annual Amount' :
                         benefit.allocated ? 'Allocated Amount' : 'Value'}
                      </div>
                      <div className="font-semibold text-gray-900">
                        {benefit.premium && formatCurrency(benefit.premium)}
                        {benefit.contribution && formatCurrency(benefit.contribution * 2)}
                        {benefit.amount && formatCurrency(benefit.amount)}
                        {benefit.allocated && `${formatCurrency(benefit.allocated)}`}
                        {benefit.availableLeaves && `${benefit.availableLeaves} days`}
                      </div>
                    </div>
                  </div>

                  {/* Special handling for different benefit types */}
                  {benefit.name === 'Flexible Benefits' && (
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700">Usage</span>
                        <span className="text-sm text-gray-600">
                          {formatCurrency(benefit.used)} / {formatCurrency(benefit.allocated)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div 
                          className="bg-apollo-primary h-2 rounded-full" 
                          style={{ width: `${(benefit.used / benefit.allocated) * 100}%` }}
                        ></div>
                      </div>
                      <div className="space-y-2">
                        {benefit.options.map((option, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">{option.name}</span>
                            <span className="text-gray-900">{formatCurrency(option.used)} / {formatCurrency(option.limit)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {benefit.name === 'Provident Fund' && (
                    <div className="mt-4">
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-green-700">Current Balance</span>
                          <span className="text-lg font-bold text-green-600">{formatCurrency(benefit.currentBalance)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {benefit.name === 'Leave Encashment' && (
                    <div className="mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-blue-600">{benefit.availableLeaves}</div>
                          <div className="text-xs text-blue-700">Available Leaves</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-green-600">{benefit.encashableLeaves}</div>
                          <div className="text-xs text-green-700">Encashable Leaves</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Features List */}
                  <div className="mt-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Key Features</div>
                    <ul className="space-y-1">
                      {benefit.features.map((feature, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Provider Info */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Provider:</span>
                      <span className="font-medium text-gray-900">{benefit.provider}</span>
                    </div>
                    {benefit.policyNumber && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">Policy Number:</span>
                        <span className="font-medium text-gray-900">{benefit.policyNumber}</span>
                      </div>
                    )}
                    {benefit.validUntil && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">Valid Until:</span>
                        <span className="font-medium text-gray-900">{benefit.validUntil}</span>
                      </div>
                    )}
                    {benefit.eligibleAfter && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">Eligible After:</span>
                        <span className="font-medium text-gray-900">{benefit.eligibleAfter}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Benefits Summary Card */}
        <div className="mt-6 sm:mt-8">
          <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent rounded-2xl p-4 sm:p-6 text-white">
            <h3 className="text-lg sm:text-xl font-bold mb-4">Benefits Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold">{formatCurrency(calculateTotalValue())}</div>
                <div className="text-apollo-primary-100 text-xs sm:text-sm">Total Annual Value</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold">{Object.values(benefitsData).filter(b => b.status === 'Active').length}</div>
                <div className="text-apollo-primary-100 text-xs sm:text-sm">Active Benefits</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold">{formatCurrency(benefitsData.flexiBenefits.allocated - benefitsData.flexiBenefits.used)}</div>
                <div className="text-apollo-primary-100 text-xs sm:text-sm">Unused Flexi Benefits</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold">{formatCurrency(benefitsData.providentFund.currentBalance)}</div>
                <div className="text-apollo-primary-100 text-xs sm:text-sm">PF Balance</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BenefitsOverview;
